/* 会员支付共享运行时：预取归因、稳定请求意图和分段计时，不预创建支付对象。 */
(() => {
  const w = window;
  const INTENTS_KEY = 'jjb_membership_checkout_intents_v1';
  const INTENT_TTL = 5 * 60 * 1000;
  const memoryIntents = new Map();
  const uid = () =>
    w.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const clock = () => w.performance.now();
  function bounded(promise, ms, fallback) {
    return new Promise((resolve, reject) => {
      const timer = w.setTimeout(() => resolve(fallback), ms);
      Promise.resolve(promise)
        .then(resolve, reject)
        .finally(() => w.clearTimeout(timer));
    });
  }
  let analyticsCache;
  function analyticsScope() {
    let consent;
    try {
      consent = w.Shopify?.customerPrivacy?.currentVisitorConsent?.();
    } catch {}
    return JSON.stringify([
      w.theme?.customerId,
      w.theme?.ga4MeasurementId,
      consent,
    ]);
  }
  function prepareAnalytics() {
    const scope = analyticsScope();
    if (analyticsCache?.scope === scope && analyticsCache.expires > Date.now())
      return analyticsCache;
    const state = { scope, values: {}, expires: Date.now() + 60_000 };
    analyticsCache = state;
    const ga = (field, key) =>
      new Promise((resolve) => {
        if (!w.theme?.ga4MeasurementId || typeof w.gtag !== 'function')
          return resolve();
        let done = false;
        const finish = (value) => {
          if (done) return;
          done = true;
          w.clearTimeout(timer);
          if (value !== undefined && value !== null && value !== '')
            state.values[key] = String(value);
          resolve();
        };
        const timer = w.setTimeout(() => finish(), 1500);
        try {
          w.gtag('get', w.theme.ga4MeasurementId, field, finish);
        } catch {
          finish();
        }
      });
    const statsig = bounded(
      Promise.resolve(w.statsigReady).then(() => {
        const context = w.statsigClient?.getContext?.();
        return context?.stableID || context?.stableId;
      }),
      1500,
    )
      .then((value) => {
        if (value) state.values.statsigStableId = String(value);
      })
      .catch(() => {});
    state.promise = Promise.all([
      ga('client_id', 'gaClientId'),
      ga('session_id', 'gaSessionId'),
      statsig,
    ]).then(() => {
      if (!Object.keys(state.values).length) state.expires = Date.now();
    });
    return state;
  }
  async function getAnalytics(entryPage, budget = 200) {
    const state = prepareAnalytics();
    await bounded(state.promise, budget);
    // 不把旧身份或撤回授权前的快照带入新请求。
    return {
      ...(state.scope === analyticsScope() ? state.values : {}),
      ...(entryPage ? { entryPage } : {}),
    };
  }
  w.document.addEventListener('visitorConsentCollected', () => {
    analyticsCache = undefined;
  });
  function readIntents() {
    try {
      const items = JSON.parse(w.sessionStorage.getItem(INTENTS_KEY) || '[]');
      if (Array.isArray(items))
        items.forEach((item) => {
          if (item?.scope && item.expires > Date.now())
            memoryIntents.set(item.scope, item);
        });
    } catch {}
    for (const [key, value] of memoryIntents)
      if (value.expires <= Date.now()) memoryIntents.delete(key);
  }
  function saveIntents() {
    try {
      w.sessionStorage.setItem(
        INTENTS_KEY,
        JSON.stringify([...memoryIntents.values()].slice(-12)),
      );
    } catch {}
  }
  function getIntent({
    plan,
    billingCycle,
    uiMode,
    environment,
    analytics,
    createSuccessUrl,
    purpose,
    giftId,
  }) {
    readIntents();
    const scope = JSON.stringify(
      purpose === 'GIFT_RESUME'
        ? [
            analyticsScope(),
            environment,
            'GIFT_RESUME',
            giftId,
            plan,
            billingCycle,
          ]
        : purpose === 'SETUP'
          ? [analyticsScope(), environment, 'SETUP']
          : [
              analyticsScope(),
              environment,
              plan,
              billingCycle,
              uiMode,
              analytics?.entryPage,
              w.location.pathname + w.location.search + w.location.hash,
            ],
    );
    let intent = memoryIntents.get(scope);
    if (!intent) {
      intent = {
        scope,
        idempotencyKey: uid(),
        successUrl: createSuccessUrl(),
        analytics: analytics || {},
        expires:
          purpose === 'SETUP' || purpose === 'GIFT_RESUME'
            ? Number.MAX_SAFE_INTEGER
            : Date.now() + INTENT_TTL,
      };
      memoryIntents.set(scope, intent);
      saveIntents();
    }
    return intent;
  }
  function retireIntent(key) {
    readIntents();
    for (const [scope, intent] of memoryIntents)
      if (intent.idempotencyKey === key) memoryIntents.delete(scope);
    saveIntents();
  }
  function createAttempt(uiMode, source, start = clock()) {
    const id = uid();
    let finished = false;
    let correlation = {};
    const emit = (phase, elapsed, outcome) => {
      try {
        return Promise.resolve(
          w.zlog?.track?.(
            'membership_checkout_phase',
            {
              ...correlation,
              attempt_id: id,
              ui_mode: uiMode,
              source,
              phase,
              duration_ms: Math.round(elapsed),
              outcome,
            },
            { monitor: true, ga4: false, statsig: false },
          ),
        ).catch(() => {});
      } catch {}
    };
    return {
      bind({ checkoutId, traceId, environment, formReused, prefetchHit } = {}) {
        if (checkoutId) correlation.checkout_id = String(checkoutId);
        if (traceId) correlation.trace_id = String(traceId);
        if (environment) correlation.environment = String(environment);
        if (typeof formReused === 'boolean')
          correlation.form_reused = formReused;
        if (typeof prefetchHit === 'boolean')
          correlation.prefetch_hit = prefetchHit;
      },
      record: emit,
      async measure(phase, operation) {
        const at = clock();
        try {
          const result = await operation();
          emit(phase, clock() - at, 'success');
          return result;
        } catch (error) {
          emit(
            phase,
            clock() - at,
            error?.name === 'AbortError' ? 'closed' : 'error',
          );
          throw error;
        }
      },
      finish(outcome) {
        if (finished) return;
        finished = true;
        return Promise.resolve(emit('total', clock() - start, outcome)).then(
          () => {
            // 先等终点入队，再发送本次分段；支付状态不等待遥测网络。
            try {
              Promise.resolve(w.zlog?.flushTracking?.()).catch(() => {});
            } catch {}
          },
        );
      },
    };
  }
  w.jjbCheckoutRuntime = {
    prepareAnalytics,
    getAnalytics,
    getIntent,
    retireIntent,
    createAttempt,
  };
})();
